'use client';

// SECTION FONCTIONNALITÉ — le bloc qui structure toute la landing.
//
// Composition volontairement simple et répétée : d'un côté le discours (numéro, titre serif,
// deux lignes, trois précisions), de l'autre un téléphone qui se PRÉSENTE quand le regard
// l'atteint — il monte, se redresse, et une ou deux cartes s'en détachent pour donner de la
// profondeur. Le côté s'inverse une section sur deux : l'œil descend en zigzag sans jamais
// s'ennuyer, mais la grille reste lisible.
//
// L'inclinaison finale (`tilt`) change à chaque section : aucun téléphone n'est posé exactement
// comme son voisin, ce qui évite l'effet catalogue.

import { useReveal } from '@/hooks/useReveal';
import { useT } from '@/hooks/useT';
import { fontStyle } from '@/theme/typography';
import { PhoneFrame } from './PhoneFrame';

export interface FeatureSectionProps {
  // Clé i18n : landing.journey.<sectionKey>.*
  sectionKey: string;
  index: number;
  // Écran affiché dans le téléphone.
  screen: React.ComponentType;
  // Inclinaison au repos, en degrés.
  tilt: number;
  // Le téléphone passe à gauche (le texte à droite).
  reversed?: boolean;
  // Lueur d'ambiance derrière le téléphone.
  glow?: 'brand' | 'action' | 'info';
  // Cartes qui débordent du cadre (2 max) — profondeur et preuve concrète.
  floats?: React.ReactNode;
}

const GLOWS = {
  brand: 'var(--rmbr-brand-muted)',
  action: 'var(--rmbr-action-muted)',
  info: 'var(--rmbr-info-muted)',
};

export function FeatureSection({
  sectionKey,
  index,
  screen: Screen,
  tilt,
  reversed = false,
  glow = 'brand',
  floats,
}: FeatureSectionProps) {
  const { t } = useT('landing');
  const ref = useReveal<HTMLDivElement>({
    threshold: 0.15,
    rootMargin: '0px 0px -12% 0px',
  });

  return (
    <section
      ref={ref}
      id={`section-${sectionKey}`}
      // `overflow-x-clip` : les cartes flottantes débordent volontairement du téléphone
      // (-left-6 / -right-6). Sur petit écran elles sortiraient de la page et créeraient un
      // défilement horizontal parasite — on les rogne plutôt que de renoncer à l'effet.
      className="mx-auto w-full max-w-6xl overflow-x-clip px-5 py-20 sm:px-8 lg:py-28"
    >
      <div
        className={`grid items-center gap-12 lg:gap-20 ${
          reversed ? 'lg:grid-cols-[0.9fr_1fr]' : 'lg:grid-cols-[1fr_0.9fr]'
        }`}
      >
        {/* ------------------------------------------------------------------ Le discours */}
        <div
          className={`rmbr-scene-text flex flex-col ${reversed ? 'lg:order-2' : ''}`}
        >
          <span
            className="flex items-center gap-3 uppercase tracking-[0.18em] text-brand"
            style={fontStyle('caption')}
          >
            {String(index + 1).padStart(2, '0')}
            <span aria-hidden className="h-px w-10 bg-brand/50" />
            {t(`journey.${sectionKey}.eyebrow`)}
          </span>

          <h2
            className="mt-4 text-balance text-text-primary"
            style={{
              fontFamily: 'var(--font-serif)',
              fontSize: 'clamp(1.9rem, 3.8vw, 2.9rem)',
              lineHeight: 1.05,
              letterSpacing: '-0.03em',
              fontWeight: 600,
            }}
          >
            {t(`journey.${sectionKey}.title`)}
          </h2>

          <p
            className="mt-4 max-w-md text-pretty text-text-secondary"
            style={fontStyle('bodyLg')}
          >
            {t(`journey.${sectionKey}.body`)}
          </p>

          <ul className="mt-6 flex flex-col gap-3">
            {[0, 1, 2].map((n) => (
              <li
                key={n}
                className="flex items-start gap-3 text-text-secondary"
                style={fontStyle('body')}
              >
                <span
                  aria-hidden
                  className="mt-[9px] size-1.5 shrink-0 rounded-full bg-brand"
                />
                {t(`journey.${sectionKey}.points.${n}`)}
              </li>
            ))}
          </ul>
        </div>

        {/* ----------------------------------------------------------------- Le téléphone */}
        <div
          className={`relative flex justify-center ${reversed ? 'lg:order-1' : ''}`}
        >
          <div
            aria-hidden
            className="pointer-events-none absolute -inset-10 -z-10 rounded-full opacity-70 blur-3xl"
            style={{
              background: `radial-gradient(circle, ${GLOWS[glow]}, transparent 70%)`,
            }}
          />

          <div
            className="rmbr-phone-enter relative"
            style={{ ['--rmbr-tilt' as string]: `${tilt}deg` }}
          >
            <div className="rmbr-phone-float">
              <PhoneFrame>
                <Screen />
              </PhoneFrame>
            </div>
            {floats}
          </div>
        </div>
      </div>
    </section>
  );
}

// Carte flottante posée en débord du téléphone. Positionnée par le parent via `className`
// (ex. `-left-8 top-16`) ; `delay` échelonne les arrivées.
export function FloatCard({
  children,
  className = '',
  delay = 750,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  return (
    <div
      className={`rmbr-float-card absolute z-20 flex items-center gap-2.5 rounded-xl border border-border bg-surface-elevated px-3 py-2.5 shadow-[var(--rmbr-shadow-xl)] ${className}`}
      style={{ ['--rmbr-stagger' as string]: `${delay}ms` }}
    >
      {children}
    </div>
  );
}
